import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, User, Building2, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { fairManagerInquiryApi } from '../services/api';
import type { FairInquiryDetailResponse, InquiryMessageCreate } from '../types';
import { extractApiError } from '../utils/errorUtils';

const FairManagerInquiryConversation: React.FC = () => {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();

  const [inquiry, setInquiry] = useState<FairInquiryDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [replyMessage, setReplyMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Fetch inquiry details
  const fetchInquiry = useCallback(async () => {
    if (!inquiryId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await fairManagerInquiryApi.getInquiryDetail(parseInt(inquiryId));
      setInquiry(data);

      // Mark messages as read (Issue #390)
      if (data.unread_count > 0) {
        await fairManagerInquiryApi.markMessagesAsRead(parseInt(inquiryId));
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to fetch inquiry:', apiError);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [inquiryId]);

  useEffect(() => {
    fetchInquiry();
  }, [fetchInquiry]);

  // Handle reply submission
  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inquiry || !replyMessage.trim()) return;

    setIsSending(true);
    setSendError(null);

    try {
      const data: InquiryMessageCreate = {
        inquiry_id: inquiry.id,
        message: replyMessage.trim(),
        is_from_vendor: false, // Fair manager replying
      };

      await fairManagerInquiryApi.addResponse(data);

      // Refresh inquiry to show new message
      await fetchInquiry();

      // Clear reply form
      setReplyMessage('');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to send message:', apiError);
      setSendError(apiError.message);
    } finally {
      setIsSending(false);
    }
  };

  // Get status badge
  const getStatusBadge = () => {
    if (!inquiry) return null;

    const badges = {
      new: {
        icon: AlertCircle,
        className: 'bg-moonbone text-ink',
        label: 'New',
      },
      in_progress: {
        icon: Clock,
        className: 'bg-yellow-100 text-yellow-800',
        label: 'In Progress',
      },
      waiting_for_vendor: {
        icon: AlertCircle,
        className: 'bg-orange-100 text-orange-800',
        label: 'Awaiting Vendor Response',
      },
      resolved: {
        icon: CheckCircle,
        className: 'bg-green-100 text-green-800',
        label: 'Resolved',
      },
      closed: {
        icon: CheckCircle,
        className: 'bg-gray-100 text-gray-800',
        label: 'Closed',
      },
    };

    const badge = badges[inquiry.status];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.className}`}>
        <Icon className="w-4 h-4 mr-1" />
        {badge.label}
      </span>
    );
  };

  // Format timestamp
  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="conversation-loading">
        <div className="flex items-center space-x-2 text-gray-600">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span>Loading conversation...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !inquiry) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="conversation-error">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
            Error Loading Inquiry
          </h2>
          <p className="text-gray-600 text-center mb-4">
            {error || 'Inquiry not found'}
          </p>
          <button
            data-testid="back-to-inquiries-button"
            onClick={() => navigate('/fairmanager/inquiries')}
            className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            Back to Inquiries
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            data-testid="back-to-inquiries-button"
            onClick={() => navigate('/fairmanager/inquiries')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Inquiries
          </button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="inquiry-subject">{inquiry.subject}</h1>
              <div className="flex items-center space-x-3 mb-3">
                <span data-testid="inquiry-status-badge">{getStatusBadge()}</span>
                <span className="text-sm px-2.5 py-0.5 rounded border bg-gray-50 text-gray-700 border-gray-200" data-testid="inquiry-priority-badge">
                  {inquiry.priority.charAt(0).toUpperCase() + inquiry.priority.slice(1)} Priority
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
                <div className="flex items-center">
                  <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                  <span><strong>Fair:</strong> {inquiry.fair_name}</span>
                </div>
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-400" />
                  <span><strong>Vendor:</strong> {inquiry.vendor_name}</span>
                </div>
                <div>
                  <strong>Type:</strong> {inquiry.inquiry_type.replace('_', ' ')}
                </div>
                <div>
                  <strong>Created:</strong> {formatTimestamp(inquiry.created_at)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6 mb-6" data-testid="messages-list">
          {inquiry.messages.map((message) => (
            <div
              key={message.id}
              data-testid="message-item"
              className={`flex ${!message.is_from_vendor ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-3xl ${!message.is_from_vendor ? 'w-full' : 'w-full'}`}>
                <div
                  className={`rounded-lg p-4 ${
                    !message.is_from_vendor
                      ? 'bg-teal-50 border border-teal-200'
                      : 'bg-white border border-gray-200 shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          !message.is_from_vendor ? 'bg-primary' : 'bg-gray-600'
                        }`}
                      >
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{message.sender_name}</div>
                        <div className="text-xs text-gray-500">
                          {!message.is_from_vendor ? 'You' : 'Vendor'}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{formatTimestamp(message.created_at)}</div>
                  </div>
                  <div className="text-gray-700 whitespace-pre-wrap">{message.message}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Reply Form */}
        {inquiry.status !== 'closed' && inquiry.status !== 'resolved' && (
          <div className="bg-white rounded-lg shadow p-6" data-testid="reply-form">
            <h3 className="font-medium text-gray-900 mb-4">Reply</h3>

            {sendError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start space-x-2" data-testid="send-error-message">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="text-sm text-red-700">{sendError}</div>
              </div>
            )}

            <form onSubmit={handleReply}>
              <textarea
                data-testid="reply-message-input"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary resize-none"
                disabled={isSending}
                required
                maxLength={2000}
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm text-gray-500" data-testid="character-count">{replyMessage.length}/2000 characters</span>
                <button
                  data-testid="send-reply-button"
                  type="submit"
                  disabled={isSending || !replyMessage.trim()}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isSending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Send Reply</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Closed/Resolved Notice */}
        {(inquiry.status === 'closed' || inquiry.status === 'resolved') && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center" data-testid="closed-inquiry-notice">
            <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-1">This inquiry is {inquiry.status}</h3>
            <p className="text-sm text-gray-600">
              You can no longer reply to this conversation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FairManagerInquiryConversation;
